import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Tab, { TabGroup } from "@amzn/meridian/tab";
import Box from "@amzn/meridian/box";
import Heading from "@amzn/meridian/heading";
import Text from "@amzn/meridian/text";
import Icon from "@amzn/meridian/icon";
import { useNavigationControl } from "../../hooks/useNavigationControl";
import { useAppSelector } from "../../store";
import { ROUTES } from "../../utils/constants";

import "./ShiftPlanTabs.scss";

interface TabItem {
  id: string;
  label: string;
  path: string;
}

// Shift plan tabs represents the different tabs that are present when a new shift plan is being generated
// i.e. roster and rates, volume and buffer and review
export interface ShiftPlanTabsProps {
  description?: string;
  date?: string;
  shiftType?: string;
  planType?: string | null;
  children: React.ReactNode;
}

const ShiftPlanTabs: React.FC<ShiftPlanTabsProps> = ({
  description,
  date,
  shiftType,
  planType,
  children,
}) => {
  const { navigate } = useNavigationControl();
  const location = useLocation();

  // Read feature flag from Redux
  const isCheckoutEnabled = useAppSelector((state) => state.checkout.isCheckoutEnabled);

  // Define the tabs for the shift planning process (conditionally include checkout)
  const tabs: TabItem[] = [
    { id: "roster", label: "1. Roster and Rates", path: ROUTES.SHIFT_PLAN_ROSTER },
    { id: "volumes", label: "2. Volumes and Buffer", path: ROUTES.SHIFT_PLAN_VOLUMES },
    ...(isCheckoutEnabled ? [{ id: "checkout", label: "3. Checkout", path: ROUTES.SHIFT_PLAN_CHECKOUT }] : []),
    {
      id: "review",
      label: isCheckoutEnabled ? "4. Review and Publish" : "3. Review and Publish",
      path: ROUTES.SHIFT_PLAN_REVIEW,
    },
  ];

  // Check if user has submitted inputs (plan generated via checkout)
  // Check if plan has been generated (Review tab enabled after Generate Plan succeeds)
  const hasSubmittedInputs = useAppSelector((state) => state.checkout.generatePlanLoading === false && state.checkout.generatePlanError === null && state.checkout.showConfirmation === false && !!state.checkout.appliedDryRunResponse);

  // Check if user has loaded checkout data (dryRunResponse exists in Redux)
  const hasCheckoutData = useAppSelector((state) => !!state.checkout.dryRunResponse);

  // Determine if we're on the review page
  const isOnReviewPage = location.pathname === ROUTES.SHIFT_PLAN_REVIEW;

  // Determine if we're on the checkout page
  const isOnCheckoutPage = location.pathname === ROUTES.SHIFT_PLAN_CHECKOUT;

  // Find the active tab based on the current path or use the first tab
  const getActiveTab = React.useCallback((): string => {
    const currentPath = location.pathname;
    const activeTab = tabs.find((tab) => tab.path === currentPath);
    return activeTab ? activeTab.id : tabs[0].id;
  }, [tabs, location.pathname]);

  const [currentTab, setCurrentTab] = React.useState(getActiveTab());

  // Update the URL when a tab is clicked using controlled navigation
  const handleTabChange = React.useCallback(
    (tabId: string) => {
      // When on review or checkout page, don't allow navigating to other tabs
      if (isOnReviewPage || isOnCheckoutPage) return;

      const selectedTab = tabs.find((tab) => tab.id === tabId);
      if (selectedTab && selectedTab.id !== "review" && selectedTab.id !== "checkout") {
        // Use controlled navigation that respects the navigation enabled state
        navigate(selectedTab.path);
        setCurrentTab(tabId);
      }
    },
    [tabs, navigate, isOnReviewPage, isOnCheckoutPage]
  );

  // Update the current tab when the location changes
  useEffect(() => {
    setCurrentTab(getActiveTab());
  }, [getActiveTab]);

  return (
    <Box spacingInset="300" className="shift-plan-tabs">
      <Box className="shift-plan-header">
        <Box className="title-section">
          <Heading type="h400" level={2}>
            Shift Plan
          </Heading>
          {description && <Text color="secondary">{description}</Text>}
        </Box>
        {date && shiftType && (
          <Box className="shift-info">
            <Text color="secondary" type="b200">
              {planType === "pdp" ? "Prior Day Plan for" : "Start of Shift Plan for"}: {date}, {shiftType}
            </Text>
          </Box>
        )}
      </Box>

      <Box className="tabs-container">
        <TabGroup
          fill="line"
          value={currentTab}
          onChange={handleTabChange}
          aria-labelledby="shift-plan-tabs"
        >
          {tabs.map((tab) => {
            const isDisabled = (isOnReviewPage && tab.id !== "review") ||
                               (isOnCheckoutPage && tab.id !== "checkout") ||
                               (tab.id === "checkout" && !isOnCheckoutPage && !hasCheckoutData) ||
                               (tab.id === "review" && !isOnReviewPage && !hasSubmittedInputs);
            return (
              <Tab
                key={tab.id}
                value={tab.id}
                aria-controls={tab.id}
                id={tab.id}
                disabled={isDisabled}
              >
                {tab.label}
              </Tab>
            );
          })}
        </TabGroup>
        {isOnReviewPage && (
          <Text type="b200" color="secondary" className="review-lock-message">
            Navigation is locked while on the Review page. Use the review tabs below to view inputs.
          </Text>
        )}
        {isOnCheckoutPage && (
          <Text type="b200" color="secondary" className="review-lock-message">
            Navigation is locked while on the Checkout page. Use Go Back to return to Volumes.
          </Text>
        )}
      </Box>

      <div className="tab-content">{children}</div>
    </Box>
  );
};

export default ShiftPlanTabs;

import './ShiftPlanTabs.scss';